import { useEffect, useState } from 'react';
import Card from '../../components/card/Card';
import H from '../../components/texts/h/H';
import P from '../../components/texts/p/P';
import Icon from '../../components/icon/Icon';
import Span from '../../components/texts/span/Span';
import Table from '../../components/table/table';
import Button from '../../components/button/Button';
import Modal from '../../components/modal/modal';
import Input from '../../components/input/Input';
import DragAndDrop from '../../components/DragAndDrop/DragAndDrop';
import { 
  getLoanMonetizationFiles, 
  downloadMonetizationFile,
  uploadMonetizationFile 
} from '../../services/MonetizationService';
import { getMimeType } from '../../utils/Utils';
import { MAX_FILE_SIZE } from '../../utils/Constants';

interface FileData {
  name: string;
  type: string;
}

interface GuaranteeAttachmentsTabProps {
  idGlobal: string;
}

const GuaranteeAttachmentsTab: React.FC<GuaranteeAttachmentsTabProps> = ({ idGlobal }) => {
  const [files, setFiles] = useState<FileData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [fileName, setFileName] = useState<string>('');
  const [fileType, setFileType] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string>('');
  const [isUploading, setIsUploading] = useState<boolean>(false);

  useEffect(() => {
    loadFiles();
  }, [idGlobal]);

  const loadFiles = async () => {
    if (!idGlobal) {
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    setError('');
    try {
      const response = await getLoanMonetizationFiles(idGlobal);
      if (response.success && 'data' in response && response.data && Array.isArray(response.data)) {
        setFiles(response.data);
      }
    } catch (error) {
      console.error('Error loading files:', error);
      setError('Error al cargar los archivos');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileView = async (fileName: string) => {
    if (!idGlobal || !fileName) return;
    
    try {
      const response = await downloadMonetizationFile(idGlobal, fileName);
      if (response.success && 'data' in response) {
        const mimeType = getMimeType(fileName);
        const fileBlob = new Blob([response.data], { type: mimeType });
        const url = window.URL.createObjectURL(fileBlob);
        
        if (mimeType.startsWith('application/pdf') || mimeType.startsWith('image/')) {
          window.open(url, '_blank');
        } else {
          const a = document.createElement('a');
          a.href = url;
          a.download = fileName;
          a.click();
          window.URL.revokeObjectURL(url);
        }
      }
    } catch (error) {
      console.error('Error viewing file:', error);
      setError('Error al visualizar el archivo');
    }
  };

  const columns = [
    {
      name: 'Nombre',
      cell: (row: FileData) => (
        <Card>
          <P type='3' variant='neutral'>
            {row.name}
          </P>
        </Card>
      ),
      minWidth: '200px',
      grow: 1.5,
    },
    {
      name: 'Tipo',
      cell: (row: FileData) => (
        <Card>
          <P type='3' variant='neutral'>
            {row.type}
          </P>
        </Card>
      ),
      minWidth: '150px',
      grow: 1,
    },
    {
      name: '',
      cell: (row: FileData) => (
        <Card display='flex' justifyContent='center'>
          <Icon
            name='view_outline'
            variant='info'
            height={44}
            cursor='pointer'
            onClick={() => handleFileView(row.name)}
          />
        </Card>
      ),
      minWidth: '60px',
      grow: 0.3,
    },
  ];

  const handleOpenModal = () => {
    setIsModalOpen(true);
    setFileName('');
    setFileType('');
    setSelectedFile(null);
    setFileError('');
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setFileName('');
    setFileType('');
    setSelectedFile(null);
    setFileError('');
  };

  const handleFileChange = (file: File) => {
    setSelectedFile(file);
    setFileError('');
  };

  const handleFileErrorChange = (error: any) => {
    if (typeof error === 'object' && error !== null) {
      if (error.message) {
        setFileError(error.message);
      } else {
        setFileError('Error al procesar el archivo');
      }
    } else if (typeof error === 'string') {
      setFileError(error);
    } else {
      setFileError('Error desconocido al procesar el archivo');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !fileName || !fileType) {
      setFileError('Por favor complete todos los campos y seleccione un archivo');
      return;
    }

    setIsUploading(true);
    setFileError('');

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('fileName', fileName);
      formData.append('type', fileType);

      const response = await uploadMonetizationFile(idGlobal, formData);
      
      if (response.success) {
        handleCloseModal();
        await loadFiles(); // Recargar la lista de archivos
      } else {
        setFileError('Error al subir el archivo');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setFileError('Error al subir el archivo');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Card spacing="4">
      <Card margin={{ bottom: '3' }} display="flex" justifyContent="space-between" alignItems="center">
        <H type='3' weight='bold' variant='neutral-1' modifier='strong' withMargin={false}>
          Archivos adjuntos
        </H>
        <Button variant='tertiary' onClick={handleOpenModal}>
          <Card display='flex' alignItems='center' gap='8px'>
            <Icon name='plus_icon' variant='info' width={20} height={20} />
            Nuevo adjunto
          </Card>
        </Button>
      </Card>

      {isLoading && (
        <Card display="flex" justifyContent="center" alignItems="center" height="200px">
          <P type="2" variant="neutral" weight="regular">
            Cargando archivos...
          </P>
        </Card>
      )}

      {!isLoading && error && (
        <Card>
          <Span variant='danger' type='2'>
            {error}
          </Span>
        </Card>
      )}

      {!isLoading && !error && files.length === 0 && (
        <Card display="flex" justifyContent="center" alignItems="center" height="200px">
          <P type="2" variant="neutral" weight="regular" modifier="weak">
            No hay archivos adjuntos
          </P>
        </Card>
      )}

      {!isLoading && !error && files.length > 0 && (
        <Table
          columns={columns}
          data={files}
          tableData={{
            size: files.length,
            page: 0,
            totalElements: files.length,
            totalPages: 1,
          }}
          handleChangePage={() => {}}
          variant='normal'
        />
      )}

      <Modal 
        isOpen={isModalOpen} 
        onClose={handleCloseModal}
        title="Nuevo adjunto"
        primaryText={isUploading ? 'Subiendo...' : 'Subir archivo'}
        primaryVariant="primary"
        onPrimaryAction={handleUpload}
        primaryActionDisabled={isUploading || !selectedFile || !fileName || !fileType}
        secondaryText="Cancelar"
        secondaryVariant="secondary"
        onSecondaryAction={handleCloseModal}
        loadingPrimaryBtn={isUploading}
        width="500px"
      >
        <Card margin={{ top: '3' }}>
          <Input
            label="Nombre"
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
            placeholder="Ingrese el nombre del archivo"
          />
        </Card>

        <Card margin={{ top: '3' }}>
          <Input
            label="Tipo"
            value={fileType}
            onChange={(e) => setFileType(e.target.value)}
            placeholder="Ingrese el tipo de archivo"
          />
        </Card>

        <Card margin={{ top: '5' }}>
          {!selectedFile ? (
            <DragAndDrop
              name='file_upload'
              setValue={handleFileChange}
              fileTypes={['pdf', 'jpg', 'jpeg', 'png', 'xls', 'xlsx', 'doc', 'docx']}
              maxFileSize={MAX_FILE_SIZE}
              showError={handleFileErrorChange}
              size='auto'
            />
          ) : (
            <Card
              display='flex'
              alignItems='center'
              gap='16px'
              spacing='3'
              variant='neutral'
              modifier='inverted'
              borderRadiusAmount='3'
              border
            >
              <Icon variant='neutral' name='document_outline' />
              <Card display='flex' justifyContent='space-between' width='100%' alignItems='center'>
                <P type='3' variant='neutral'>
                  {selectedFile.name}
                </P>
                <Icon
                  name='delete_outline'
                  variant='neutral'
                  height={44}
                  cursor='pointer'
                  onClick={() => {
                    setSelectedFile(null);
                    setFileError('');
                  }}
                />
              </Card>
            </Card>
          )}
        </Card>

        {fileError && (
          <Card margin={{ top: '2' }}>
            <Span variant='danger' type='2'>
              {fileError}
            </Span>
          </Card>
        )}
      </Modal>
    </Card>
  );
};

export default GuaranteeAttachmentsTab;
